import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shopifyAPI, SyncHistory } from '../services/api';
import { format, subDays, formatDistanceStrict } from 'date-fns';

interface SyncProgress {
  isRunning: boolean;
  phase: 'idle' | 'counting' | 'fetching' | 'importing' | 'complete' | 'error';
  totalExpectedOrders: number;
  ordersFetched: number;
  batchesFetched: number;
  ordersImported: number;
  transactionsImported: number;
  message: string;
  error?: string;
}

const SyncShopify: React.FC = () => {
  const [searchParams] = useSearchParams();
  
  // Read URL parameters for start_date and end_date
  const urlStartDate = searchParams.get('start_date');
  const urlEndDate = searchParams.get('end_date');
  
  const [startDate, setStartDate] = useState(
    urlStartDate || format(subDays(new Date(), 30), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(
    urlEndDate || format(new Date(), 'yyyy-MM-dd')
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Sync history state
  const [syncHistory, setSyncHistory] = useState<SyncHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Fetch sync history
  const fetchSyncHistory = useCallback(async () => {
    try {
      const response = await shopifyAPI.getSyncHistory(20);
      setSyncHistory(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch sync history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);
  
  // Load sync history on mount
  useEffect(() => {
    fetchSyncHistory();
  }, [fetchSyncHistory]);
  
  // Update dates when URL parameters change
  useEffect(() => {
    if (urlStartDate) setStartDate(urlStartDate);
    if (urlEndDate) setEndDate(urlEndDate);
  }, [urlStartDate, urlEndDate]);

  // Poll for progress updates
  useEffect(() => {
    if (loading && progress?.isRunning) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const response = await shopifyAPI.getSyncProgress();
          const newProgress = response.data.data;
          setProgress(newProgress);
          
          // Stop polling if sync is complete
          if (!newProgress.isRunning) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setLoading(false);
            
            // Show results
            if (newProgress.error) {
              setError(newProgress.error);
            } else {
              setResult({
                message: newProgress.message,
                ordersImported: newProgress.ordersImported,
                transactionsImported: newProgress.transactionsImported,
              });
            }
            
            // Refresh sync history
            fetchSyncHistory();
          }
        } catch (err) {
          console.error('Failed to fetch progress:', err);
        }
      }, 2000); // Poll every 2 seconds to avoid rate limiting
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [loading, progress?.isRunning]);

  const handleSync = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setProgress({
        isRunning: true,
        phase: 'counting',
        totalExpectedOrders: 0,
        ordersFetched: 0,
        batchesFetched: 0,
        ordersImported: 0,
        transactionsImported: 0,
        message: 'Starting import...',
      });

      const response = await shopifyAPI.syncOrders(startDate, endDate);
      
      if (!response.data.success) {
        setError(response.data.error || 'Failed to start sync');
        setLoading(false);
        setProgress(null);
      }
      // Continue polling for progress
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sync Shopify data');
      setLoading(false);
      setProgress(null);
    }
  };

  // Calculate progress percentage
  const getProgressPercent = () => {
    if (!progress) return 0;
    if (progress.totalExpectedOrders === 0) return 0;
    return Math.min(100, Math.round((progress.ordersImported / progress.totalExpectedOrders) * 100));
  };

  return (
    <div>
      {/* Progress Modal */}
      {loading && progress && (
        <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)' }}>
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-2xl)',
            padding: 'var(--space-8)',
            width: '100%',
            maxWidth: '520px',
            boxShadow: 'var(--shadow-xl)',
            border: '1px solid var(--color-neutral-200)',
            animation: 'fadeIn 0.3s ease-out',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Animated gradient border at top */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, var(--color-primary-400), var(--color-info), var(--color-primary-400))',
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s linear infinite',
            }} />
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 'var(--space-4)',
              marginBottom: 'var(--space-6)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius-xl)',
                background: 'linear-gradient(135deg, var(--color-primary-100) 0%, var(--color-primary-200) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-600)" strokeWidth="2" style={{ animation: 'spin 2s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
              </div>
              <div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: 'var(--text-xl)',
                  fontWeight: 700,
                  color: 'var(--color-neutral-900)',
                  letterSpacing: '-0.02em'
                }}>
                  Importing Data
                </h2>
                <p style={{ 
                  margin: 0, 
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-neutral-500)'
                }}>
                  Syncing from Shopify
                </p>
              </div>
            </div>
            
            {/* Progress Bar */}
            {progress.totalExpectedOrders > 0 && (
              <div style={{ marginBottom: 'var(--space-5)' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontSize: 'var(--text-sm)', 
                  marginBottom: 'var(--space-2)',
                  color: 'var(--color-neutral-600)'
                }}>
                  <span style={{ fontWeight: 500 }}>Progress</span>
                  <span style={{ 
                    fontWeight: 700,
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--color-primary-600)'
                  }}>
                    {getProgressPercent()}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '10px',
                  background: 'var(--color-neutral-100)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${getProgressPercent()}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, var(--color-primary-400) 0%, var(--color-primary-600) 100%)',
                    borderRadius: 'var(--radius-full)',
                    transition: 'width 0.3s ease',
                    boxShadow: '0 0 12px var(--color-primary-400)',
                  }} />
                </div>
              </div>
            )}
            
            {/* Status Message */}
            <div style={{ 
              marginBottom: 'var(--space-5)',
              padding: 'var(--space-4)',
              background: 'var(--color-neutral-50)',
              borderRadius: 'var(--radius-lg)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-neutral-700)',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              border: '1px solid var(--color-neutral-200)',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-500)" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              {progress.message}
            </div>
            
            {/* Stats Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: 'var(--space-4)',
              marginBottom: 'var(--space-5)',
            }}>
              <div style={{ 
                padding: 'var(--space-5)',
                background: 'linear-gradient(135deg, var(--color-primary-50) 0%, var(--color-primary-100) 100%)',
                borderRadius: 'var(--radius-xl)',
                textAlign: 'center',
                border: '1px solid var(--color-primary-200)',
              }}>
                <div style={{ 
                  fontSize: 'var(--text-3xl)', 
                  fontWeight: 700, 
                  color: 'var(--color-primary-700)',
                  fontFamily: 'var(--font-mono)',
                  lineHeight: 1.2,
                }}>
                  {progress.ordersImported.toLocaleString()}
                </div>
                <div style={{ 
                  fontSize: 'var(--text-xs)', 
                  color: 'var(--color-primary-600)', 
                  marginTop: 'var(--space-1)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  Orders Imported
                </div>
              </div>
              <div style={{ 
                padding: 'var(--space-5)',
                background: 'linear-gradient(135deg, var(--color-info-light) 0%, #bfdbfe 100%)',
                borderRadius: 'var(--radius-xl)',
                textAlign: 'center',
                border: '1px solid #93c5fd',
              }}>
                <div style={{ 
                  fontSize: 'var(--text-3xl)', 
                  fontWeight: 700, 
                  color: '#1e40af',
                  fontFamily: 'var(--font-mono)',
                  lineHeight: 1.2,
                }}>
                  {progress.transactionsImported.toLocaleString()}
                </div>
                <div style={{ 
                  fontSize: 'var(--text-xs)', 
                  color: '#1e40af', 
                  marginTop: 'var(--space-1)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  Transactions
                </div>
              </div>
            </div>
            
            {/* Batch Info */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-neutral-500)',
              padding: 'var(--space-3) 0',
              borderTop: '1px solid var(--color-neutral-200)',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="3" y1="9" x2="21" y2="9"/>
                  <line x1="9" y1="21" x2="9" y2="9"/>
                </svg>
                Batches: <strong style={{ marginLeft: '2px' }}>{progress.batchesFetched}</strong>
              </span>
              {progress.totalExpectedOrders > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                  </svg>
                  Expected: <strong style={{ marginLeft: '2px' }}>~{progress.totalExpectedOrders.toLocaleString()}</strong>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="page-header">
        <h1>Sync Shopify Data</h1>
        <p>Import orders and transactions from your Shopify store</p>
      </div>

      <div className="card">
        <form onSubmit={handleSync}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Importing...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 2v6h-6"/>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
                  <path d="M3 22v-6h6"/>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
                </svg>
                Import Orders
              </>
            )}
          </button>
        </form>
      </div>

      {error && (
        <div className="error" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          {error}
        </div>
      )}

      {result && !loading && (
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, var(--color-success-light) 0%, #bbf7d0 100%)',
          border: '1px solid var(--color-success)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: 'var(--radius-full)',
              background: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'var(--shadow-md)',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#166534', fontSize: 'var(--text-lg)' }}>Import Complete!</h3>
              <p style={{ margin: 0, color: '#15803d', fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>
                {result.message || `Imported ${result.ordersImported?.toLocaleString()} orders and ${result.transactionsImported?.toLocaleString()} transactions`}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-info)" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          Instructions
        </h2>
        <ol style={{ 
          paddingLeft: 'var(--space-6)', 
          lineHeight: '2',
          color: 'var(--color-neutral-600)',
        }}>
          <li>Select the date range for orders you want to import</li>
          <li>Click "Import Orders" to fetch data from Shopify</li>
          <li>Orders and transactions are imported in batches of 250</li>
          <li>Progress updates in real-time as each batch completes</li>
          <li>Existing orders will be updated if they already exist</li>
          <li>Import is crash-resilient - completed batches are saved immediately</li>
        </ol>
      </div>

      {/* Previous Imports History */}
      <div className="card">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-neutral-600)" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Previous Imports
        </h2>
        
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-neutral-500)' }}>
            Loading history...
          </div>
        ) : syncHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-neutral-500)' }}>
            No import history yet. Run your first import above.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Date Range</th>
                  <th>Orders</th>
                  <th>Transactions</th>
                  <th>Duration</th>
                  <th>Triggered By</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.map((sync) => {
                  const startedAt = new Date(sync.started_at);
                  const completedAt = sync.completed_at ? new Date(sync.completed_at) : null;
                  const duration = completedAt 
                    ? formatDistanceStrict(startedAt, completedAt)
                    : sync.status === 'running' ? 'In progress...' : '-';
                  
                  const dateRange = sync.start_date && sync.end_date
                    ? `${format(new Date(sync.start_date), 'MMM d, yyyy')} - ${format(new Date(sync.end_date), 'MMM d, yyyy')}`
                    : sync.start_date
                    ? `From ${format(new Date(sync.start_date), 'MMM d, yyyy')}`
                    : '-';

                  const getStatusBadge = (status: string) => {
                    const statusClasses: Record<string, string> = {
                      'completed': 'status-reconciled',
                      'running': 'status-partial',
                      'failed': 'status-variance',
                      'cancelled': 'status-pending',
                    };
                    return (
                      <span className={`status-badge ${statusClasses[status] || ''}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    );
                  };

                  return (
                    <tr key={sync.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{format(startedAt, 'MMM d, yyyy')}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-neutral-500)' }}>
                          {format(startedAt, 'h:mm a')}
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(sync.status)}
                        {sync.error_message && (
                          <div style={{ 
                            fontSize: 'var(--text-xs)', 
                            color: 'var(--color-error)', 
                            marginTop: 'var(--space-1)',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }} title={sync.error_message}>
                            {sync.error_message}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 'var(--text-sm)' }}>{dateRange}</td>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>
                        {sync.orders_imported.toLocaleString()}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>
                        {sync.transactions_imported.toLocaleString()}
                      </td>
                      <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-neutral-600)' }}>
                        {duration}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)',
                          fontSize: 'var(--text-xs)',
                          padding: 'var(--space-1) var(--space-2)',
                          background: sync.triggered_by === 'cron' ? 'var(--color-primary-50)' : 'var(--color-neutral-100)',
                          color: sync.triggered_by === 'cron' ? 'var(--color-primary-700)' : 'var(--color-neutral-600)',
                          borderRadius: 'var(--radius-md)',
                          fontWeight: 500,
                        }}>
                          {sync.triggered_by === 'cron' ? (
                            <>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                              </svg>
                              Scheduled
                            </>
                          ) : (
                            <>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                              </svg>
                              Manual
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncShopify;
